import { type FC, useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, Clock, MapPin, Users, Shield, DollarSign, Trophy, ArrowLeft, X, ListOrdered } from 'lucide-react';
import { Button, Card, CardHeader, CardTitle, CardContent, Skeleton } from '@/components/ui';
import { supabase } from '@/lib/api/supabase';
import type { Tournament } from '@/types';
import { dbToTournament } from '@/types';
import { useAuth } from '@/hooks';
import { useWaitlist } from '@/hooks/data/useWaitlist';
import { TournamentGallery } from '@/components/tournaments/TournamentGallery';
import { getActivePrice } from '@/lib/utils/stripe';
import { REGISTRATIONS_CLOSED } from '@/lib/registrationsClosed';
import { ClosedNotice } from '@/components/shared';

/**
 * Tournament detail page with hero gallery and comprehensive information
 */
export const TournamentDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getWaitlistCount } = useWaitlist();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [waitingCount, setWaitingCount] = useState(0);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void getWaitlistCount(id).then((count) => {
      if (!cancelled) setWaitingCount(count);
    });
    return () => { cancelled = true; };
  }, [id, getWaitlistCount]);

  useEffect(() => {
    const fetchTournament = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('tournaments')
          .select('*')
          .eq('id', id)
          .single();

        if (fetchError) throw fetchError;

        if (data) {
          setTournament(dbToTournament(data));
        }
      } catch (err) {
        console.error('Failed to fetch tournament:', err);
        setError(err instanceof Error ? err.message : 'Failed to load tournament');
      } finally {
        setLoading(false);
      }
    };

    void fetchTournament();
  }, [id]);

  const handleRegister = () => {
    if (!user) {
      navigate('/login', { state: { returnTo: `/event-registration?tournament=${id}` } });
      return;
    }
    navigate('/event-registration', { state: { preselectedTournamentId: id } });
  };

  const formatDate = (date: string): string => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (time: string | null): string => {
    if (!time) return 'TBD';
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatCurrency = (cents: number): string => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const capitalizeWords = (str: string): string => {
    return str.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
        {/* Hero Gallery Skeleton */}
        <Skeleton className="w-full h-[400px] md:h-[500px] rounded-none" />

        <div className="container mx-auto px-4 py-8 -mt-20 relative z-10">
          {/* Back Button Skeleton */}
          <Skeleton className="h-10 w-40 mb-4" />

          {/* Main Content Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Details Skeleton */}
            <div className="lg:col-span-2 space-y-6">
              {/* Title Card Skeleton */}
              <Card className="backdrop-blur-md bg-turquoise-800/90">
                <CardHeader>
                  <Skeleton className="h-9 w-2/3 mb-3" />
                  <div className="flex flex-wrap gap-2">
                    <Skeleton className="h-7 w-24" />
                    <Skeleton className="h-7 w-24" />
                    <Skeleton className="h-7 w-20" />
                  </div>
                </CardHeader>
              </Card>

              {/* Description Skeleton */}
              <Card className="backdrop-blur-md bg-turquoise-800/90">
                <CardHeader>
                  <Skeleton className="h-6 w-48 mb-2" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                </CardContent>
              </Card>

              {/* Rules Skeleton */}
              <Card className="backdrop-blur-md bg-turquoise-800/90">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-5 w-5" />
                    <Skeleton className="h-6 w-20" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Info Skeleton */}
            <div className="space-y-6">
              <Card className="backdrop-blur-md bg-turquoise-800/90">
                <CardHeader>
                  <Skeleton className="h-6 w-40" />
                </CardHeader>
                <CardContent className="space-y-4">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Skeleton className="h-5 w-5 flex-shrink-0" />
                      <div className="flex-1">
                        <Skeleton className="h-4 w-20 mb-1" />
                        <Skeleton className="h-5 w-full" />
                      </div>
                    </div>
                  ))}
                  <div className="pt-4 border-t border-white/20">
                    <Skeleton className="h-11 w-full" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !tournament) {
    return (
      <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">
            <div className="rounded-lg bg-red-900/40 border border-red-500/40 p-6 text-red-200 backdrop-blur-md shadow-lg inline-block">
              <p className="font-semibold">Error loading tournament</p>
              <p className="mt-2 text-sm">{error || 'Tournament not found'}</p>
              <Link to="/events">
                <Button className="mt-4" variant="secondary">
                  Back to Events
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const images = [tournament.headerImageUrl, ...tournament.galleryImages].filter(Boolean) as string[];

  return (
    <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
      {/* Hero Gallery */}
      {images.length > 0 && (
        <div className="relative w-full h-[400px] md:h-[500px] bg-turquoise-900">
          <img
            src={images[0]}
            alt={tournament.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-turquoise-900/90 via-turquoise-900/50 to-transparent" />
        </div>
      )}

      <div className="container mx-auto px-4 py-8 -mt-20 relative z-10">
        {/* Back Button */}
        <Link to="/events">
          <Button variant="ghost" className="text-white hover:bg-white/10 mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Events
          </Button>
        </Link>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title Card */}
            <Card className="backdrop-blur-md bg-turquoise-800/90">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-3xl font-viking text-white mb-2">
                      {tournament.name}
                    </CardTitle>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-orange-500 text-white rounded-full text-sm font-medium">
                        {capitalizeWords(tournament.weapon)}
                      </span>
                      <span className="px-3 py-1 bg-turquoise-600 text-white rounded-full text-sm font-medium">
                        {capitalizeWords(tournament.division)}
                      </span>
                      <span className="px-3 py-1 bg-white/20 text-white rounded-full text-sm font-medium">
                        {capitalizeWords(tournament.status)}
                      </span>
                    </div>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Description */}
            {tournament.description && (
              <Card className="backdrop-blur-md bg-turquoise-800/90">
                <CardHeader>
                  <CardTitle className="text-white text-xl">About This Tournament</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-white/90 whitespace-pre-wrap">{tournament.description}</p>
                </CardContent>
              </Card>
            )}

            {/* Rules */}
            {tournament.rules && (
              <Card className="backdrop-blur-md bg-turquoise-800/90">
                <CardHeader>
                  <CardTitle className="text-white text-xl flex items-center gap-2">
                    <Shield className="h-5 w-5 text-orange-400" />
                    Rules
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-white/90 whitespace-pre-wrap">{tournament.rules}</p>
                </CardContent>
              </Card>
            )}

            {/* Gallery */}
            {images.length > 1 && (
              <Card className="backdrop-blur-md bg-turquoise-800/90">
                <CardHeader>
                  <CardTitle className="text-white text-xl">Gallery</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {images.slice(1).map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImageIndex(index + 1)}
                        className="relative h-32 rounded-lg overflow-hidden hover:opacity-80 transition-opacity"
                      >
                        <img
                          src={img}
                          alt={`Gallery image ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Info & Registration */}
          <div className="space-y-6">
            {/* Quick Info */}
            <Card className="backdrop-blur-md bg-turquoise-800/90 sticky top-4">
              <CardHeader>
                <CardTitle className="text-white text-xl">Tournament Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-orange-300 font-medium">Date</p>
                    <p className="text-white">{formatDate(tournament.date)}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-orange-300 font-medium">Time</p>
                    <p className="text-white">
                      {formatTime(tournament.startTime)} - {formatTime(tournament.endTime)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-orange-300 font-medium">Location</p>
                    <p className="text-white">{tournament.location}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Users className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-orange-300 font-medium">Participants</p>
                    <p className="text-white">
                      {tournament.currentParticipants} / {tournament.maxParticipants}
                    </p>
                    <div className="w-full bg-white/20 rounded-full h-2 mt-2">
                      <div
                        className="bg-orange-500 h-2 rounded-full transition-all"
                        style={{
                          width: `${(tournament.currentParticipants / tournament.maxParticipants) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {tournament.waitlistHeldSpots > 0 && (
                  <div className="flex items-start gap-3">
                    <ListOrdered className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-orange-300 font-medium">Waitlist</p>
                      <p className="text-white">
                        {tournament.waitlistHeldSpots} {tournament.waitlistHeldSpots === 1 ? 'spot' : 'spots'} held for waitlist
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <DollarSign className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  {(() => {
                    const { price, isEarlyBird } = getActivePrice(tournament);
                    return (
                      <div>
                        <p className="text-sm text-orange-300 font-medium">Registration Fee</p>
                        <div className="flex items-center gap-2">
                          <p className={`text-xl font-bold ${isEarlyBird ? 'text-green-400' : 'text-white'}`}>
                            {formatCurrency(price)}
                          </p>
                          {isEarlyBird && tournament.registrationFee > price && (
                            <p className="text-sm line-through text-white/50">
                              {formatCurrency(tournament.registrationFee)}
                            </p>
                          )}
                        </div>
                        {isEarlyBird && (
                          <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-bold bg-green-500/90 text-white">
                            Early Bird Pricing
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>

                <div className="pt-4 border-t border-white/20">
                  {(() => {
                    if (REGISTRATIONS_CLOSED) {
                      return <ClosedNotice className="text-center text-white/80" />;
                    }

                    const effectiveUsed = tournament.currentParticipants + tournament.waitlistHeldSpots + waitingCount;
                    const isAtCapacity = effectiveUsed >= tournament.maxParticipants;
                    const isFullOrAtCapacity = tournament.status === 'full' || isAtCapacity;

                    if (tournament.status === 'completed') {
                      return (
                        <Button
                          disabled
                          className="w-full"
                          size="lg"
                        >
                          <Trophy className="h-5 w-5 mr-2" />
                          Tournament Completed
                        </Button>
                      );
                    }

                    if (isFullOrAtCapacity) {
                      return (
                        <Button
                          onClick={handleRegister}
                          className="w-full bg-red-600 hover:bg-red-700"
                          size="lg"
                        >
                          <ListOrdered className="h-5 w-5 mr-2" />
                          Full, Waitlist Open
                        </Button>
                      );
                    }

                    return (
                      <Button
                        onClick={handleRegister}
                        className="w-full"
                        size="lg"
                      >
                        <Trophy className="h-5 w-5 mr-2" />
                        Register Now
                      </Button>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Tournament Gallery */}
        <TournamentGallery
          currentTournamentId={tournament.id}
          weapon={tournament.weapon}
          division={tournament.division}
          limit={6}
        />
      </div>

      {/* Image Lightbox */}
      {selectedImageIndex !== null && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImageIndex(null)}
        >
          <button
            onClick={() => setSelectedImageIndex(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="h-6 w-6 text-white" />
          </button>
          <img
            src={images[selectedImageIndex]}
            alt={`Gallery image ${selectedImageIndex}`}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};
